import type { ColDef } from 'ag-grid-community';

// A column size strategy (GridColumnSizeSettings: performance boards, Driver
// Setup) as DataTable lays it out, matching what AG's autoSizeStrategy does
// with it:
// - none: every column at AG's default width
// - fitGridWidth: the columns share the width, each within its limits (the
//   strategy's defaults, or the column's own), refitting as it resizes. A
//   column with a width of its own (a selection checkbox) keeps it
// - fitCellContents: the chosen columns (or all of them) as wide as their
//   cells, headers too unless skipped; the others at their own width
const toDataTableSizing = ({
  strategy,
  colDefs,
}: {
  strategy: FleetOps.ColumnSizeStrategies.Strategy | undefined;
  colDefs: ColDef[];
}): {
  sizing: DataTable.SizingMode;
  columnDefs: ColDef[];
  fittedColumnIds?: string[];
  skipHeaderWhenSizing?: boolean;
} => {
  if (!strategy) {
    return { sizing: 'fixed', columnDefs: colDefs };
  }

  if (strategy.type === 'fitGridWidth') {
    return {
      sizing: 'fitWidth',
      columnDefs: colDefs.map((colDef) => {
        if (colDef.width !== undefined) {
          return colDef;
        }
        const limit = strategy.columnLimits.find(
          (l) => l.field === colDef.field,
        );
        return {
          ...colDef,
          flex: 1,
          minWidth: limit?.minWidth ?? strategy.defaultMinWidth,
          maxWidth: limit?.maxWidth ?? strategy.defaultMaxWidth,
        };
      }),
    };
  }

  const fields = strategy.fields ?? [];
  return fields.length > 0
    ? {
        sizing: 'fixed',
        columnDefs: colDefs,
        fittedColumnIds: fields,
        skipHeaderWhenSizing: strategy.skipHeader,
      }
    : {
        sizing: 'fitContent',
        columnDefs: colDefs,
        skipHeaderWhenSizing: strategy.skipHeader,
      };
};

export default toDataTableSizing;
